import { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { FiCalendar, FiAlertTriangle } from 'react-icons/fi'
import { FaTshirt } from 'react-icons/fa'

import api from '../../../utils/api'
import Button from '../../../shared/components/Button/Button'
import NumberToMoney from '../../../shared/functions/NumberToMoney'
import getCostForCloth, { colorHasAnyCost } from '../../../shared/functions/getCostForCloth'
import getRefForCloth from '../../../shared/functions/getRefForCloth'

import './ColorProviderReport.css'

// Cada cor pode ter várias faixas de custo (por quantidade) — mesma fonte/formato do
// ListCostRow.js ("Entre X e Y unidades"), só reaproveitado aqui em forma de lista curta.
function formatCostTiers(cost) {
    if (!cost || cost.length === 0) return []
    return cost.map((c) => `${c.minUnit}–${c.maxUnit} un: R$ ${NumberToMoney(c.cost)}`)
}

function ColorProviderReport() {
    const { providerId } = useParams()
    const [provider, setProvider] = useState(null)
    const [colors, setColors] = useState([])
    const [load, setLoad] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    useEffect(() => {
        const token = localStorage.getItem('token') || ''
        api.get('color/getall', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } })
            .then((response) => {
                const foundProvider = (response.data.providers || []).find((p) => p._id === providerId)
                if (!foundProvider) {
                    setErrorMessage('Fornecedor não encontrado')
                    setLoad(true)
                    return
                }
                setProvider(foundProvider)
                setColors((response.data.colors || []).filter((c) => c.provider?._id === providerId))
                setLoad(true)
                document.title = `Tabela de Preços - ${foundProvider.name}`
            })
            .catch(() => {
                setErrorMessage('Não foi possível carregar o relatório')
                setLoad(true)
            })
    }, [providerId])

    const groups = useMemo(() => {
        // Uma cor pode existir em vários tecidos — entra em cada grupo de tecido a que pertence.
        const list = []
        const indexByCloth = {}
        colors.forEach((item) => {
            const itemCloths = item.cloths && item.cloths.length > 0 ? item.cloths : [{ _id: 'sem-tecido', name: 'Sem tecido' }]
            itemCloths.forEach((cloth) => {
                const key = cloth._id
                if (indexByCloth[key] === undefined) {
                    indexByCloth[key] = list.length
                    list.push({ clothId: key, clothName: cloth.name, items: [] })
                }
                list[indexByCloth[key]].items.push(item)
            })
        })
        list.forEach((g) => g.items.sort((a, b) => a.name.localeCompare(b.name)))
        list.sort((a, b) => a.clothName.localeCompare(b.clothName))
        return list
    }, [colors])

    const colorsWithoutCostCount = useMemo(() => colors.filter((c) => !colorHasAnyCost(c)).length, [colors])

    if (!load) {
        return <div className="pg-loading">Carregando relatório...</div>
    }

    if (errorMessage) {
        return <div className="pg-empty"><p className="pg-empty-title">{errorMessage}</p></div>
    }

    return (
        <div className="cpr-page">
            <div className="cpr-toolbar cpr-no-print pg-sticky-top">
                <Button variant="print" onClick={() => window.print()}>Imprimir / Exportar PDF</Button>
            </div>

            <div className="cpr-print-area">
                <div className="cpr-header">
                    <div className="cpr-header-title">
                        <p className="cpr-title">Tabela de Preços — {provider.name}</p>
                        <span className="cpr-title-accent" />
                    </div>
                    <div className="cpr-header-meta">
                        <span><FiCalendar size={11} /> Gerado em {new Date().toLocaleDateString('pt-BR')}</span>
                        <span>{groups.length} {groups.length === 1 ? 'tecido' : 'tecidos'}</span>
                        <span>{colors.length} {colors.length === 1 ? 'cor' : 'cores'}</span>
                    </div>
                </div>

                {colorsWithoutCostCount > 0 && (
                    <div className="cpr-alert-bar">
                        <FiAlertTriangle size={14} className="cpr-alert-bar-icon" />
                        <span>
                            {colorsWithoutCostCount} {colorsWithoutCostCount === 1 ? 'cor sem preço definido' : 'cores sem preço definido'}
                        </span>
                    </div>
                )}

                {groups.length === 0 ? (
                    <div className="pg-empty">
                        <p className="pg-empty-title">Nenhuma cor cadastrada pra esse fornecedor</p>
                    </div>
                ) : (
                    groups.map((group) => (
                        <div key={group.clothId} className="cpr-cloth-section">
                            <p className="cpr-cloth-title"><FaTshirt size={13} /> {group.clothName}</p>
                            <table className="cpr-table">
                                <thead>
                                    <tr>
                                        <th className="cpr-table-left">Cor</th>
                                        <th className="cpr-table-left">Tipo</th>
                                        <th className="cpr-table-left">Ref</th>
                                        <th className="cpr-table-left">Faixas de Custo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.items.map((item) => {
                                        const clothIdForLookup = group.clothId === 'sem-tecido' ? null : group.clothId
                                        const tiers = formatCostTiers(getCostForCloth(item, clothIdForLookup))
                                        return (
                                            <tr key={item._id}>
                                                <td className="cpr-table-left">{item.name}</td>
                                                <td className="cpr-table-left">{item.colorType?.name || '—'}</td>
                                                <td className="cpr-table-left">{getRefForCloth(item, clothIdForLookup) || '—'}</td>
                                                <td className="cpr-table-left">
                                                    {tiers.length > 0 ? (
                                                        <div className="cpr-tier-list">
                                                            {tiers.map((t, i) => <span key={i} className="cpr-tier">{t}</span>)}
                                                        </div>
                                                    ) : (
                                                        <span className="cpr-no-cost">sem custo definido</span>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ))
                )}
            </div>
        </div>
    )
}

export default ColorProviderReport
